import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useWizardContext } from "./AppointmentWizardContext";
import { useWizardSteps, WIZARD_STEPS } from "./hooks/useWizardSteps";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarCheck,
  faCheckCircle,
  faBellConcierge,
  faCalendarLinesPen,
} from "@fortawesome/pro-solid-svg-icons";
import { useSnackbarMessage } from "../Snackbar.jsx";

function useHandleButtonClick(currentAppointmentId) {
  const { t } = useTranslation();
  const displaySnackbarMessage = useSnackbarMessage();
  const {
    activeStep,
    isTemplateFormValid,
    carryOverNextAppointmentNote,
    handleFormSubmit,
  } = useWizardContext();
  const { isStepSkipped, handleProgressStep } = useWizardSteps();

  return React.useCallback(
    async function () {
      if (!currentAppointmentId) return;

      try {
        if (
          activeStep === WIZARD_STEPS.PROCEDURE_NOTES &&
          !isStepSkipped(WIZARD_STEPS.PROCEDURE_NOTES)
        ) {
          if (!isTemplateFormValid || carryOverNextAppointmentNote === null) {
            return;
          }
          const success = await handleFormSubmit();
          if (!success) return;
          displaySnackbarMessage({
            title: t("Zápis z ošetření byl úspěšně odeslán."),
          });
        }

        await handleProgressStep();
      } catch (error) {
        console.error("Wizard progression error:", error);
      }
    },
    [
      t,
      displaySnackbarMessage,
      activeStep,
      isTemplateFormValid,
      carryOverNextAppointmentNote,
      handleFormSubmit,
      isStepSkipped,
      handleProgressStep,
    ],
  );
}

const AppointmentWizard = () => {
  const { t } = useTranslation();
  const { activeStep, currentAppointmentId, isCompleting } = useWizardContext();

  const { getStepConfig, isStepSkipped } = useWizardSteps();
  const [rtgWarningOpen, setRtgWarningOpen] = useState(false);

  const handleButtonClick = useHandleButtonClick(currentAppointmentId);

  const handleCreateRtgAndProceed = () => {
    setRtgWarningOpen(false);
    alert("Prosím vytvořte AD HOC RTG appointment a poté pokračujte.");
  };

  const allSteps = [
    {
      stepNumber: WIZARD_STEPS.PREPARATION,
      label: t("wizard.steps.preparation"),
      icon: faCalendarCheck,
    },
    {
      stepNumber: WIZARD_STEPS.COMPLETION,
      label: t("wizard.steps.completion"),
      icon: faCheckCircle,
    },
    {
      stepNumber: WIZARD_STEPS.RECEPTION_NOTES,
      label: t("wizard.steps.receptionNotes"),
      icon: faBellConcierge,
    },
    {
      stepNumber: WIZARD_STEPS.PROCEDURE_NOTES,
      label: t("wizard.steps.procedureNotes"),
      icon: faCalendarLinesPen,
    },
  ];

  const visibleSteps = useMemo(
    () => allSteps.filter((step) => !isStepSkipped(step.stepNumber)),
    [isStepSkipped],
  );

  const getVisualStepIndex = (stepNumber) =>
    visibleSteps.findIndex((step) => step.stepNumber === stepNumber);

  const currentVisualIndex = getVisualStepIndex(activeStep);

  const isAdvanceDisabled =
    !getStepConfig(activeStep).canProgress &&
    activeStep !== WIZARD_STEPS.RECEPTION_NOTES;

  return (
    <>
      <Box
        sx={(theme) => ({
          display: isCompleting ? "none" : "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 3,
          position: "fixed",
          bottom: "20px",
          left: "50%",
          transform: "translateX(-50%)",
          width: "90%",
          maxWidth: 1000,
          padding: 2,
          backgroundColor: "background.paper",
          borderRadius: 2,
          zIndex: 1000,
          flexWrap: "wrap",
          boxShadow: theme.shadows[1],
        })}
      >
        {visibleSteps.map((step, visualIndex) => (
          <Box
            key={step.stepNumber}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              color:
                currentVisualIndex === visualIndex
                  ? "primary.main"
                  : "text.secondary",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: 40,
                height: 40,
                borderRadius: "50%",
                backgroundColor:
                  currentVisualIndex === visualIndex
                    ? "primary.main"
                    : "grey.100",
                color:
                  currentVisualIndex === visualIndex
                    ? "background.default"
                    : "text.secondary",
              }}
            >
              <FontAwesomeIcon icon={step.icon} size="lg" />
            </Box>
            <Typography
              data-test-id="step-label"
              data-test-active={
                currentVisualIndex === visualIndex ? "true" : "false"
              }
              variant="caption"
              sx={{
                fontWeight:
                  currentVisualIndex === visualIndex ? "bold" : "normal",
                marginTop: "4px",
                fontSize: "1rem",
              }}
            >
              {step.label}
            </Typography>
          </Box>
        ))}

        <Button
          variant="contained"
          color="primary"
          disabled={isAdvanceDisabled}
          sx={{
            minWidth: 200,
            padding: "10px 20px",
            fontSize: "1.4rem",
            fontWeight: "bold",
          }}
          {...(activeStep === WIZARD_STEPS.PROCEDURE_NOTES && {
            form: "jsonform",
            type: "submit",
          })}
          {...(activeStep === WIZARD_STEPS.RECEPTION_NOTES &&
          !isStepSkipped(activeStep)
            ? {
                form: "appointmentWizard",
                type: "submit",
              }
            : { onClick: handleButtonClick })}
        >
          {activeStep === WIZARD_STEPS.RECEPTION_NOTES &&
          !isStepSkipped(WIZARD_STEPS.RECEPTION_NOTES)
            ? t("appointmentNotes.sendToReceptionist")
            : getStepConfig(activeStep).nextLabel}
        </Button>
      </Box>

      {/* RTG/TELE warning */}
      <Dialog
        open={rtgWarningOpen}
        onClose={() => setRtgWarningOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t("rtg.warningTitle", "Vyžadováno RTG/TELE")}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t(
              "rtg.warningMessage",
              "Pro tento úkon je povinné RTG nebo TELE...",
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRtgWarningOpen(false)}>
            {t("common.cancel", "Zrušit")}
          </Button>
          <Button onClick={handleCreateRtgAndProceed} variant="contained">
            {t("rtg.createRtgAppointment", "Vytvořit RTG appointment")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AppointmentWizard;
